import { ROUTER_API } from 'app/constants/api.constant';
import { AmountType } from 'app/main/design-system/models/coupon.type';
import { ListObject } from 'app/main/design-system/models/listObjectsModel';
import BaseApi from 'app/services/api/base.api';
import {
  AddManualClientPayload,
  ClientDTO,
  DailyAppsMember,
  GoogleReviewDTO,
  GoogleReviewSnapshotDTO,
  GoogleReviewsCountersDTO,
  LeaderboardRowDTO,
  ReviewFilter,
  ReviewLinkChoice,
  ReviewLinkSettings,
  ReviewQrLinkDTO,
  SendReviewQrSmsPayload,
  ShareChannel,
  SmsConfigDTO,
  SnapshotPayload,
} from '../type';

const USE_MOCK = false;

const STORES_PATH = 'api/v2/stores';
const DAILYAPPS_PATH = 'api/v2/dailyapps';

export const googleReviewsApi = new BaseApi({ baseURL: ROUTER_API, tokenKey: 'token', timeout: 60000 });

const DEMO_REVIEWS: GoogleReviewDTO[] = [
  {
    id: '1',
    name: 'Christiano Ronaldo',
    firstName: 'Client',
    rating: 3,
    opinion: 'Muchas gracias aficione , SSUUIIIIIII',
    opinionDate: '2026-04-01',
    source: 'littlebill',
    isConfirmedOnGoogle: false,
    isSmsSent: false,
    isManual: false,
  },
  {
    id: '2',
    name: 'Michel Souquet',
    firstName: 'Michel',
    rating: 5,
    opinion: 'Excellent accueil, je recommande vivement !',
    opinionDate: '2026-03-25',
    source: 'littlebill',
    isConfirmedOnGoogle: true,
    isSmsSent: true,
    isManual: false,
  },
  {
    id: '3',
    name: 'Abderrahim Tuibi',
    firstName: 'Abderrahim',
    rating: 5,
    opinion: 'Très bonne boulangerie, produits frais.',
    opinionDate: '2026-03-24',
    source: 'littlebill',
    isConfirmedOnGoogle: false,
    isSmsSent: false,
    isManual: false,
  },
  {
    id: '4',
    name: 'Lionel Messi',
    firstName: 'Client',
    rating: 5,
    opinion: 'Que mira bobo',
    opinionDate: '2026-03-24',
    source: 'nfc',
    isConfirmedOnGoogle: false,
    isSmsSent: false,
    isManual: false,
  },
  {
    id: '5',
    name: 'Sophie Martin',
    firstName: 'Sophie',
    rating: 4,
    opinion: 'Bon café du matin !',
    opinionDate: '2026-03-22',
    source: 'littlebill',
    isConfirmedOnGoogle: false,
    isSmsSent: true,
    isManual: false,
  },
];

const DEMO_CLIENTS: ClientDTO[] = [
  { id: 'c1', firstName: 'Sophie', lastName: 'Martin', phone: '06 12 34 56 78', visits: 14 },
  { id: 'c2', firstName: 'Thomas', lastName: 'Bernard', phone: '07 23 45 67 89', visits: 8 },
  { id: 'c3', firstName: 'Isabelle', lastName: 'Leroy', phone: '06 98 76 54 32', visits: 22 },
  { id: 'c4', firstName: 'Kevin', lastName: 'Moreau', phone: '07 11 22 33 44', visits: 5 },
  { id: 'c5', firstName: 'Nathalie', lastName: 'Dubois', phone: '06 55 44 33 22', visits: 31 },
];

const DEMO_SMS_CONFIG: SmsConfigDTO = {
  title: 'Laissez-nous un avis Google ! 🙏',
  message: 'Bonjour {prénom}, merci pour votre visite! Votre avis Google nous aiderait beaucoup 🙏 → {lien_google}',
  googleLink: 'https://maps.google.com/?cid=VOTRE_CID',
  reward: {
    enabled: true,
    couponData: {
      title: '1 viennoiserie + 1 boisson chaude offerte 🎁',
      terms: 'Sur présentation de ce SMS en caisse.',
      data: { type: AmountType.PRODUCT, value: '1 viennoiserie + boisson' },
      validityPeriod: '2W',
    },
  },
};

const delay = (ms: number) => new Promise(resolve => setTimeout(resolve, ms));

const applyFilter = (reviews: GoogleReviewDTO[], filter: ReviewFilter) => {
  switch (filter) {
    case 'pending':
      return reviews.filter(review => !review.isConfirmedOnGoogle);
    case 'google':
      return reviews.filter(review => review.isConfirmedOnGoogle);
    case 'manual':
      return reviews.filter(review => review.isManual);
    case 'no-sms':
      return reviews.filter(review => !review.isSmsSent);
    case 'form':
      return reviews.filter(review => review.source === 'form');
    default:
      return reviews;
  }
};

export const getCountersApi = async (storeId: string): Promise<GoogleReviewsCountersDTO> => {
  if (USE_MOCK) {
    await delay(300);
    return {
      total: DEMO_REVIEWS.length,
      confirmed: DEMO_REVIEWS.filter(review => review.isConfirmedOnGoogle).length,
      pending: DEMO_REVIEWS.filter(review => !review.isConfirmedOnGoogle).length,
      smsSent: DEMO_REVIEWS.filter(review => review.isSmsSent).length,
      scans: 0,
      shares: 0,
    };
  }
  const response = await googleReviewsApi.api.get<GoogleReviewsCountersDTO>(`${STORES_PATH}/opinions/google/counters`, {
    params: { storeId },
  });
  return response.data;
};

export const getReviewsApi = async (
  storeId: string,
  page: number,
  size: number,
  filter: ReviewFilter,
): Promise<ListObject<GoogleReviewDTO>> => {
  if (USE_MOCK) {
    await delay(400);
    const filtered = applyFilter(DEMO_REVIEWS, filter);
    const start = page * size;
    return {
      data: filtered.slice(start, start + size),
      metadata: { page, size, total: filtered.length },
    };
  }
  const response = await googleReviewsApi.api.get<ListObject<GoogleReviewDTO>>(`${STORES_PATH}/opinions/google`, {
    params: { storeId, page, size, filter },
  });
  return response.data;
};

export const setGoogleConfirmedApi = async (storeId: string, reviewId: string, confirmed: boolean): Promise<void> => {
  if (USE_MOCK) {
    await delay(150);
    return;
  }
  await googleReviewsApi.api.patch(
    `${STORES_PATH}/opinions/${reviewId}/google-confirm`,
    { confirmed },
    { params: { storeId } },
  );
};

export const sendSmsApi = async (storeId: string, reviewId: string, config: SmsConfigDTO): Promise<void> => {
  if (USE_MOCK) {
    await delay(600);
    return;
  }
  await googleReviewsApi.api.post(`${STORES_PATH}/opinions/${reviewId}/send-sms`, { config }, { params: { storeId } });
};

export const addManualClientApi = async (
  storeId: string,
  payload: AddManualClientPayload,
): Promise<GoogleReviewDTO> => {
  if (USE_MOCK) {
    await delay(300);
    const fullName = [payload.firstName, payload.lastName, payload.company].filter(Boolean).join(' ');
    return {
      id: `manual-${Date.now()}`,
      name: fullName,
      firstName: payload.firstName,
      company: payload.company,
      rating: 0,
      opinion: '',
      opinionDate: new Date().toISOString(),
      source: 'manual',
      isConfirmedOnGoogle: false,
      isSmsSent: false,
      isManual: true,
    };
  }
  const response = await googleReviewsApi.api.post<GoogleReviewDTO>(`${STORES_PATH}/opinions/google/manual`, payload, {
    params: { storeId },
  });
  return response.data;
};

export const getClientsApi = async (storeId: string): Promise<ClientDTO[]> => {
  if (USE_MOCK) {
    await delay(200);
    return DEMO_CLIENTS;
  }
  const response = await googleReviewsApi.api.get<ListObject<ClientDTO>>(`${STORES_PATH}/clients`, {
    params: { storeId },
  });
  return response.data.data;
};

export const getSmsConfigApi = async (storeId: string): Promise<SmsConfigDTO> => {
  if (USE_MOCK) {
    await delay(200);
    return DEMO_SMS_CONFIG;
  }
  const response = await googleReviewsApi.api.get<SmsConfigDTO>(`${STORES_PATH}/opinions/google/sms-config`, {
    params: { storeId },
  });
  return response.data;
};

export const saveSmsConfigApi = async (storeId: string, config: SmsConfigDTO): Promise<void> => {
  if (USE_MOCK) {
    await delay(200);
    return;
  }

  const reward = { ...config.reward };
  delete reward.couponId;
  const body: SmsConfigDTO = { ...config, reward };
  await googleReviewsApi.api.put(`${STORES_PATH}/opinions/google/sms-config`, body, { params: { storeId } });
};

export const getReviewLink = async (storeId: string): Promise<ReviewLinkSettings> => {
  const response = await googleReviewsApi.api.get<ReviewLinkSettings>(`${STORES_PATH}/review-link`, {
    params: { storeId },
  });
  return response.data;
};

export const updateReviewLink = async (storeId: string, choice: ReviewLinkChoice): Promise<ReviewLinkSettings> => {
  const response = await googleReviewsApi.api.put<ReviewLinkSettings>(
    `${STORES_PATH}/review-link`,
    { choice },
    { params: { storeId } },
  );
  return response.data;
};

export const getReviewQrLink = async (storeId: string): Promise<ReviewQrLinkDTO> => {
  const response = await googleReviewsApi.api.get<ReviewQrLinkDTO>(`${STORES_PATH}/opinions/google/qr`, {
    params: { storeId },
  });
  return response.data;
};

export const sendReviewQrSms = async (storeId: string, payload: SendReviewQrSmsPayload): Promise<GoogleReviewDTO> => {
  const response = await googleReviewsApi.api.post<GoogleReviewDTO>(`${STORES_PATH}/opinions/google/qr/sms`, payload, {
    params: { storeId },
  });
  return response.data;
};

export const recordReviewShare = async (storeId: string, channel: ShareChannel): Promise<void> => {
  await googleReviewsApi.api.post(`${STORES_PATH}/opinions/google/shares`, { channel }, { params: { storeId } });
};

export const getReviewLeaderboardApi = async (storeId: string): Promise<LeaderboardRowDTO[]> => {
  const response = await googleReviewsApi.api.get<ListObject<LeaderboardRowDTO>>(
    `${STORES_PATH}/opinions/google/leaderboard`,
    { params: { storeId } },
  );
  return response.data.data;
};

export const getSnapshotsApi = async (storeId: string): Promise<GoogleReviewSnapshotDTO[]> => {
  const response = await googleReviewsApi.api.get<ListObject<GoogleReviewSnapshotDTO>>(
    `${STORES_PATH}/opinions/google/snapshots`,
    { params: { storeId } },
  );
  return response.data.data;
};

export const createSnapshotApi = async (storeId: string, payload: SnapshotPayload): Promise<void> => {
  await googleReviewsApi.api.post(`${STORES_PATH}/opinions/google/snapshots`, payload, { params: { storeId } });
};

export const updateSnapshotApi = async (
  storeId: string,
  snapshotId: string,
  payload: SnapshotPayload,
): Promise<void> => {
  await googleReviewsApi.api.patch(`${STORES_PATH}/opinions/google/snapshots/${snapshotId}`, payload, {
    params: { storeId },
  });
};

export const deleteSnapshotApi = async (storeId: string, snapshotId: string): Promise<void> => {
  await googleReviewsApi.api.delete(`${STORES_PATH}/opinions/google/snapshots/${snapshotId}`, {
    params: { storeId },
  });
};

export const getDailyAppsMembers = async (storeId: string): Promise<DailyAppsMember[]> => {
  const response = await googleReviewsApi.api.get<{ items: DailyAppsMember[] }>(`${DAILYAPPS_PATH}/members`, {
    params: { storeId },
  });
  return response.data.items;
};

export const addDailyAppsMember = async (storeId: string, email: string): Promise<DailyAppsMember> => {
  const response = await googleReviewsApi.api.post<DailyAppsMember>(
    `${DAILYAPPS_PATH}/members`,
    { email },
    { params: { storeId } },
  );
  return response.data;
};

export const removeDailyAppsMember = async (storeId: string, memberId: string): Promise<void> => {
  await googleReviewsApi.api.delete(`${DAILYAPPS_PATH}/members/${memberId}`, { params: { storeId } });
};
